import { motion } from "framer-motion";
import { ArrowUpRight, Facebook, Search } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export default function AppsShowcase() {
  return (
    <section id="apps" className="py-24 md:py-32" data-testid="apps-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-neutral-500">01 — The apps</p>
          <h2 className="font-heading font-medium tracking-tight text-3xl md:text-4xl text-neutral-900 mt-4 max-w-2xl">
            One brand. A growing family of AI ad managers.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-8 mt-14">
          <motion.a
            href="https://social.5ads.ai"
            target="_blank"
            rel="noopener noreferrer"
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            className="group relative bg-white border border-neutral-200 rounded-2xl p-8 md:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_16px_50px_rgb(0,0,0,0.08)] hover:-translate-y-1 transition-all duration-300"
            data-testid="app-card-social"
          >
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-2xl bg-[#1877F2]/10 flex items-center justify-center">
                <Facebook size={22} className="text-[#1877F2]" />
              </div>
              <span className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-emerald-700 bg-emerald-50 border border-emerald-200 px-3 py-1.5 rounded-full" data-testid="badge-live">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
              </span>
            </div>
            <h3 className="font-heading text-xl md:text-2xl font-medium text-neutral-900 mt-8">
              social<span className="text-neutral-400">.5ads.ai</span>
            </h3>
            <p className="text-neutral-600 leading-relaxed mt-3">
              The Facebook &amp; Instagram ads manager. A three-person AI team designs creatives,
              writes copy, optimizes budgets and scores leads — you just approve.
            </p>
            <div className="mt-8 overflow-hidden rounded-xl border border-neutral-200">
              <img
                src="https://images.unsplash.com/photo-1686061592689-312bbfb5c055?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
                alt="social.5ads.ai analytics"
                className="w-full h-48 object-cover group-hover:scale-[1.03] transition-transform duration-500"
              />
            </div>
            <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-neutral-900">
              Open the app
              <ArrowUpRight size={16} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200 text-[#FF4D00]" />
            </span>
          </motion.a>

          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: 0.1 }}
            className="relative bg-neutral-50 border border-dashed border-neutral-300 rounded-2xl p-8 md:p-10"
            data-testid="app-card-search"
          >
            <div className="flex items-start justify-between">
              <div className="w-12 h-12 rounded-2xl bg-neutral-200/60 flex items-center justify-center">
                <Search size={22} className="text-neutral-500" />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#FF4D00] bg-[#FF4D00]/5 border border-[#FF4D00]/25 px-3 py-1.5 rounded-full" data-testid="badge-coming-soon">
                Coming soon
              </span>
            </div>
            <h3 className="font-heading text-xl md:text-2xl font-medium text-neutral-700 mt-8">
              search<span className="text-neutral-400">.5ads.ai</span>
            </h3>
            <p className="text-neutral-500 leading-relaxed mt-3">
              The Google Ads manager. Same AI team, pointed at Search — keywords, bids,
              responsive search ads and quality scores, all drafted for your approval.
            </p>
            <div className="mt-8 rounded-xl border border-neutral-200 bg-white h-48 flex flex-col items-center justify-center gap-3">
              <div className="flex items-center gap-2 font-mono text-xs text-neutral-400 uppercase tracking-[0.2em]">
                <span className="w-2 h-2 rounded-full bg-[#FF4D00]/60" /> In the lab
              </div>
              <p className="text-sm text-neutral-500">Launching on 5ads.ai soon</p>
            </div>
            <span className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-neutral-400">
              Stay tuned
            </span>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
